import { useFeatureFlag } from './useFeatureFlag';
import { useEffectiveSubscriptionType } from './useEffectiveSubscriptionType';

/**
 * The ProDowngrade rule: a case that holds Pro-only data is read-only for a team that is no longer
 * Pro. It can only be copied or have its documents downloaded.
 *
 * Keyed off what the case actually CONTAINS rather than the subscription type it was saved under.
 * A team on a Pro trial stamps every case it touches, so the older saved-type rule would have
 * locked cases that never used a single Pro feature.
 *
 * An unknown verdict (the backfill has not reached this case) is never locked.
 *
 * Pure and exported so the rule is testable without React. Mirrors
 * SubscriptionTypes.IsProDowngradeReadOnly on the server.
 */
export function isProDowngradeLocked(
    flagEnabled: boolean,
    effectiveSubscriptionType: string | undefined,
    containsProData: boolean | null | undefined,
): boolean {
    if (!flagEnabled) return false;
    if (effectiveSubscriptionType === 'pro') return false;
    return containsProData === true;
}

/**
 * True when the given case must be treated as read-only because the team is no longer Pro.
 *
 * @param containsProData CaseSummary.cpd from the case list, or the X-Case-Contains-Pro-Data
 *   header value from a case load. Undefined means not yet computed and never locks.
 */
export function useIsProDowngradeLocked(containsProData: boolean | null | undefined): boolean {
    const flagEnabled = useFeatureFlag('ProDowngrade');
    const effectiveSubscriptionType = useEffectiveSubscriptionType();
    return isProDowngradeLocked(flagEnabled, effectiveSubscriptionType, containsProData);
}
